import { useMemo } from "react";
import { iTableConfigItem } from "@components/Table/TableConfig";
import dayjs from "dayjs";
import { CustomBenchmarkSearchData } from "@api/useCustomBenchmarkSearch";
import TableRowCheck from "@components/Table/TableRowCheck";
import CategoryFilterHead from "@pages/ConceptLocker/ConceptLockerCustomBenchmarkSetPopup/CategoryFilterHead";

export enum SortingColumns {
    OriginalOrder = 'index',
    Date = 'dateNum',
    Chain = 'chainName',
    Name = 'name',
    Description = 'description',
    Price = 'priceNum',
    Category = 'category',
}

interface iCategoryFilter {
    options: string[];
    value: string[];
    onChange: (value: string[]) => void;
    isDisabled: boolean;
}

export function useCustomBenchmarkSetTableConfig(
    data: NonNullable<CustomBenchmarkSearchData['items']>,
    categoryFilter: iCategoryFilter,
) {
    return useMemo(
        (): iTableConfigItem[] => [
            {
                key: 'check',
                title: '',
                Value: ({index}) => (
                    <>
                        <TableRowCheck
                            option={{
                                id: data[index]?.id as number,
                                title: data[index]?.name as string,
                            }}
                            isPermanentlyShown
                            style={{
                                marginRight: 0
                            }}
                        />
                    </>
                ),
                styles: {
                    cell: {
                        flex: '0 0 30px',
                    },
                },
            },
            {
                key: SortingColumns.Date,
                title: 'Date',
                Value: ({index}) => (
                    <>
                        {dayjs(data[index]?.date).format('MMM YYYY')}
                    </>
                ),
                sortingColName: SortingColumns.Date,
                styles: {
                    cell: {
                        flex: '0 0 100px',
                    },
                    head: {
                        paddingLeft: 20,
                    }
                },
            },
            {
                key: SortingColumns.Chain,
                title: 'Chain',
                Value: ({index}) => (
                    <div className="lines-3">
                        {data[index]?.chainName}
                    </div>
                ),
                sortingColName: SortingColumns.Chain,
                styles: {
                    cell: {
                        flex: '0 0 200px',
                    },
                },
            },
            {
                key: SortingColumns.Name,
                title: 'Concept name',
                Value: ({index}) => (
                    <div className="lines-3">
                        {data[index]?.name}
                    </div>
                ),
                sortingColName: SortingColumns.Name,
                styles: {
                    cell: {
                        flex: '0 0 250px',
                    },
                },
            },
            {
                key: SortingColumns.Description,
                title: 'Description',
                Value: ({index}) => (
                    <div className="lines-3">
                        {data[index]?.description || ''}
                    </div>
                ),
                sortingColName: SortingColumns.Description,
                styles: {
                    cell: {
                        flex: '0 1 100%',
                    },
                },
            },
            {
                key: SortingColumns.Price,
                title: 'Price',
                Value: ({index}) => (
                    <>{data[index]?.price ?? '-'}</>
                ),
                sortingColName: SortingColumns.Price,
                styles: {
                    cell: {
                        flex: '0 0 100px',
                        justifyContent: 'center',
                    },
                },
            },
            {
                key: SortingColumns.Category,
                title: (
                    <CategoryFilterHead
                        title="Item Category"
                        options={categoryFilter.options}
                        value={categoryFilter.value}
                        onChange={categoryFilter.onChange}
                        isDisabled={categoryFilter.isDisabled}
                    />
                ),
                Value: ({index}) => (
                    <div className="lines-3">
                        {data[index]?.itemCategory}
                    </div>
                ),
                sortingColName: SortingColumns.Category,
                styles: {
                    cell: {
                        flex: '0 0 200px',
                    },
                },
            },
        ],
        [data, categoryFilter]
    );
}